/* eslint-disable react-hooks/exhaustive-deps */
import React, { useRef, useEffect, useCallback, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { useReactToPrint } from 'react-to-print';
import msgNotificacion from '../../scripts/notification';
import LogoFS from '../../assets/images/logoFS.png';
import Formatter from '../../scripts/formatters';
import { mesesEnPeriodo, construirFilasReporte } from '../../utils/reporteDepartamentoPlanVacacion';
import '../../styles/solicitudes.css';
import '../../styles/planVacaciones.css';

const cellSx = {
  border: '1px solid #ddd',
  backgroundColor: '#fff',
  color: '#212529',
};

const headerCellSx = {
  ...cellSx,
  fontWeight: 'bold',
  backgroundColor: '#f5f5f5',
  color: '#212529',
  fontSize: '0.72rem',
  lineHeight: 1.2,
  whiteSpace: 'normal',
  wordWrap: 'break-word',
  padding: '4px 3px !important',
};

const headerCellSxCompact = {
  ...headerCellSx,
  fontSize: '0.65rem',
};

const FormReporteDepartamentoPlanVacacionPdf = ({ reporte, print, setPrint }) => {
  const componentRef = useRef(null);

  const handleAfterPrint = useCallback(() => {
    msgNotificacion('Impresión exitosa', 'success', 500);
    setPrint(false);
  }, [setPrint]);

  const handleBeforeContent = useCallback(() => {
    msgNotificacion(
      'En el diálogo de impresión: escala 100%, desactiva "Encabezados y pies de página" y usa orientación horizontal.',
      'info',
      4500
    );
  }, []);

  const pageStyle = `
    @page {
      size: letter landscape;
      margin: 10mm 12mm 12mm 12mm;
    }
    @media print {
      html, body {
        height: auto;
        margin: 0 !important;
        padding: 0 !important;
        background: #fff !important;
        color: #212529 !important;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      .reporte-depto-print,
      .reporte-depto-print .MuiPaper-root,
      .reporte-depto-print .MuiTableCell-root {
        background-color: #fff !important;
        color: #212529 !important;
      }
      .reporte-depto-print .MuiTableHead .MuiTableCell-root {
        background-color: #f5f5f5 !important;
      }
    }
  `;

  const handlePrint = useReactToPrint({
    documentTitle: ' ',
    content: () => componentRef.current,
    onAfterPrint: handleAfterPrint,
    onBeforeGetContent: handleBeforeContent,
    pageStyle,
  });

  const triggerPrint = useCallback(() => {
    if (componentRef.current) {
      setTimeout(() => {
        handlePrint();
      }, 500);
    }
  }, [handlePrint]);

  useEffect(() => {
    if (print) {
      triggerPrint();
    }
  }, [print, triggerPrint]);

  const encabezado = reporte?.encabezado || {};
  const meses = useMemo(
    () => mesesEnPeriodo(encabezado.FechaInicio, encabezado.FechaFin),
    [encabezado.FechaInicio, encabezado.FechaFin]
  );
  const filas = useMemo(
    () => construirFilasReporte(reporte?.empleados, reporte?.dias, meses),
    [reporte?.empleados, reporte?.dias, meses]
  );

  const deptNombre = (encabezado.NombreDepartamento || 'DEPARTAMENTO').toUpperCase();
  const periodoNombre = encabezado.NombrePeriodo || '';
  const fechaIni = encabezado.FechaInicio ? Formatter.dateFormatter(encabezado.FechaInicio) : '';
  const fechaFin = encabezado.FechaFin ? Formatter.dateFormatter(encabezado.FechaFin) : '';

  const renderCeldaFechas = (cel) => {
    if (!cel?.fechas?.length) return '—';
    return (
      <div className="reporte-depto-fechas-stack">
        {cel.fechas.map((fecha, idx) => (
          <span key={`${fecha}-${idx}`} className="reporte-depto-fechas-stack__item">
            {fecha}
          </span>
        ))}
      </div>
    );
  };

  const renderCeldaDiasMes = (cel) => {
    if (!cel?.total) return '—';
    return cel.total.toFixed(2);
  };

  const numCols = 3 + meses.length * 2 + 3;

  return (
    <div className="no-print">
      <div ref={componentRef} className="print-container reporte-depto-print">
        <div className="reporte-depto-print__header">
          <img src={LogoFS} alt="Logo FS" className="reporte-depto-print__logo" />
          <div className="reporte-depto-print__titulo">
            <div className="reporte-depto-print__titulo-main">
              Programación plan de vacaciones — {deptNombre}
            </div>
            <div className="reporte-depto-print__titulo-sub">
              {periodoNombre}
              {fechaIni && fechaFin ? ` · ${fechaIni} — ${fechaFin}` : ''}
            </div>
          </div>
        </div>

        <div className="reporte-depto-print__content">
        <TableContainer
          component={Paper}
          variant="outlined"
          className="reporte-depto-print__table-wrap"
          sx={{ backgroundColor: '#fff', boxShadow: 'none', border: 'none' }}
        >
          <Table size="small" className="reporte-depto-table">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell className="col-nombre" rowSpan={2} sx={headerCellSx}>Nombre</TableCell>
                <TableCell className="col-cargo" rowSpan={2} sx={headerCellSx}>Cargo</TableCell>
                <TableCell className="col-num" rowSpan={2} align="right" sx={headerCellSxCompact}>
                  Saldo ini.
                </TableCell>
                {meses.map((m) => (
                  <TableCell key={m.key} className="col-mes-grupo" colSpan={2} align="center" sx={headerCellSx}>
                    {m.label}
                  </TableCell>
                ))}
                <TableCell className="col-num" rowSpan={2} align="right" sx={headerCellSxCompact}>
                  Días acum.
                </TableCell>
                <TableCell className="col-num" rowSpan={2} align="right" sx={headerCellSxCompact}>
                  Total desc.
                </TableCell>
                <TableCell className="col-num col-saldo-final" rowSpan={2} align="right" sx={headerCellSxCompact}>
                  Saldo fin.
                </TableCell>
              </TableRow>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                {meses.map((m) => (
                  <React.Fragment key={`${m.key}-sub`}>
                    <TableCell className="col-mes-fechas" align="center" sx={headerCellSxCompact}>Fechas</TableCell>
                    <TableCell className="col-mes-dias" align="center" sx={headerCellSxCompact}>Días</TableCell>
                  </React.Fragment>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={numCols} align="center" sx={cellSx}>
                    Sin planes registrados para este departamento y período.
                  </TableCell>
                </TableRow>
              ) : (
                filas.map((row) => (
                  <TableRow key={row.CodEmpleado} className="reporte-depto-print__fila" sx={{ backgroundColor: '#fff' }}>
                    <TableCell className="col-nombre" sx={cellSx}>{row.NombreEmpleado}</TableCell>
                    <TableCell className="col-cargo" sx={cellSx}>{row.Cargo || '—'}</TableCell>
                    <TableCell className="col-num" align="right" sx={cellSx}>
                      {row.saldoInicial.toFixed(2)}
                    </TableCell>
                    {meses.map((m) => (
                      <React.Fragment key={m.key}>
                        <TableCell className="col-mes-fechas" sx={cellSx}>
                          {renderCeldaFechas(row.porMes[m.key])}
                        </TableCell>
                        <TableCell className="col-mes-dias" align="right" sx={cellSx}>
                          {renderCeldaDiasMes(row.porMes[m.key])}
                        </TableCell>
                      </React.Fragment>
                    ))}
                    <TableCell className="col-num" align="right" sx={cellSx}>
                      {row.diasAcumular.toFixed(2)}
                    </TableCell>
                    <TableCell className="col-num" align="right" sx={cellSx}>
                      {row.totalDescansados.toFixed(2)}
                    </TableCell>
                    <TableCell className="col-num col-saldo-final" align="right" sx={{ ...cellSx, fontWeight: 'bold' }}>
                      {row.saldoFinal.toFixed(2)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        </div>

        <div className="reporte-depto-print__firmas">
          <div className="reporte-depto-print__firma">
            <div className="reporte-depto-print__firma-label">Entregado por:</div>
            <div className="reporte-depto-print__firma-linea">
              {encabezado.JefeEntrega || '_______________________________'}
            </div>
            {encabezado.JefeEntregaCargo ? (
              <div className="reporte-depto-print__firma-cargo">{encabezado.JefeEntregaCargo}</div>
            ) : null}
          </div>
          <div className="reporte-depto-print__firma">
            <div className="reporte-depto-print__firma-label">Recibido por:</div>
            <div className="reporte-depto-print__firma-linea">
              {encabezado.JefeRecibe || '_______________________________'}
            </div>
            <div className="reporte-depto-print__firma-cargo">
              {encabezado.JefeRecibeCargo || 'Recursos Humanos'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormReporteDepartamentoPlanVacacionPdf;
